import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, ChevronDown, ChevronRight, ChevronUp, Clock, SearchX,
} from 'lucide-react';

import api from '@/lib/api';
import { formatIST, istDayKey, istDayLabel } from '@/lib/utils';
import {
  EmptyState, ScreenLoader, SearchBar, StatusPill, TopBar,
} from '@/components/phone/primitives';
import { Colors } from '@/components/phone/theme';

/**
 * What this supervisor has passed — one row per job card, not per stage.
 *
 * The movement ledger raw would give a single carton walked from Plate Making to
 * Dispatch six near-identical rows: same product, same customer, same pieces,
 * differing only in which two stage names sat in the middle. So the card is the
 * row. The hops ride inside it, collapsed to a trail (`Plate Making → Dispatch`)
 * and a count, and open on a press for when the supervisor has to show
 * management exactly what was moved and when.
 *
 * Pieces are the quantity that walked the line, not the sum of every hop — 1,000
 * through six stages is 1,000.
 *
 * Mirrors `mobile-frontend/app/job-card-history.tsx`.
 */
export default function SupervisorPassedHistoryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [cards, setCards] = useState([]);
  const [search, setSearch] = useState('');
  const [openCard, setOpenCard] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await api.get('/supervisor/my-movements', {
        params: { days: 30, ...(search ? { search } : {}) },
      });
      setCards(res.data.cards || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load what you passed');
    }
    setLoading(false);
  }, [search]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const sections = useMemo(() => {
    const byDay = new Map();
    for (const card of cards) {
      if (!byDay.has(card.day)) byDay.set(card.day, []);
      byDay.get(card.day).push(card);
    }
    // the endpoint sorts newest first, so insertion order is the order
    return [...byDay.entries()].map(([day, data]) => ({
      key: day,
      title: istDayLabel(day),
      moves: data.reduce((n, card) => n + card.moves.length, 0),
      data,
    }));
  }, [cards]);

  const today = istDayKey(new Date());
  const todayCount = sections.find((s) => s.key === today)?.data.length ?? 0;

  const subtitle = search
    ? `${cards.length} ${cards.length === 1 ? 'match' : 'matches'}`
    : cards.length === 0
      ? t('supervisor.last_30_days')
      : `${todayCount} today · ${cards.length} in 30 days`;

  return (
    <div className="flex flex-1 flex-col">
      <TopBar
        title={t('supervisor.passed_history')}
        subtitle={subtitle}
        onBack={() => navigate('/supervisor/profile')}
      />

      <div className="px-5 pt-3">
        <SearchBar
          testID="history-search"
          placeholder={t('supervisor.search_placeholder')}
          onChange={setSearch}
        />
      </div>

      {loading ? <ScreenLoader /> : cards.length === 0 ? (
        <EmptyState
          icon={search ? SearchX : Clock}
          message={search ? t('supervisor.no_matches') : t('supervisor.nothing_passed')}
        />
      ) : (
        <div data-testid="passed-list" className="px-5 pb-12">
          {sections.map((section) => (
            <section key={section.key}>
              <div className="mt-5 flex items-baseline justify-between gap-3">
                <h2 className="text-[15px] font-bold -tracking-[0.2px]" style={{ color: Colors.text.primary }}>
                  {section.title}
                </h2>
                <p className="text-[11px] font-medium" style={{ color: Colors.text.tertiary }}>
                  {section.data.length} {section.data.length === 1 ? 'job card' : 'job cards'} ·{' '}
                  {section.moves} {section.moves === 1 ? 'stage' : 'stages'}
                </p>
              </div>
              <ul>
                {section.data.map((card) => (
                  <PassedCard
                    key={card.id}
                    card={card}
                    t={t}
                    open={openCard === card.id}
                    onToggle={() => setOpenCard(openCard === card.id ? null : card.id)}
                    onOpenJobCard={() => navigate(`/supervisor/job-card/${card.job_card_id}`)}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function PassedCard({ card, t, open, onToggle, onOpenJobCard }) {
  const stock = card.order_kind === 'stock';
  // where it came in and where it got to — the whole walk in one line
  const start = card.moves[0]?.from_stage_name || '';
  const trail = card.last_stage ? `${start || '—'} → ${card.last_stage}` : start;

  return (
    <li
      data-testid={`passed-${card.id}`}
      className="mt-3 rounded-[10px] border px-4 py-3"
      style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
    >
      <button
        type="button"
        data-testid={`passed-head-${card.id}`}
        onClick={onToggle}
        className="flex w-full items-center gap-2 text-left"
      >
        <span className="min-w-0 flex-1">
          <span className="line-clamp-2 block text-[15px] font-bold -tracking-[0.2px]" style={{ color: Colors.text.primary }}>
            {card.item_name || 'Untitled item'}
          </span>
          <span className="mt-0.5 block truncate text-[13px] font-medium" style={{ color: Colors.text.secondary }}>
            {stock ? t('supervisor.internal_stock') : (card.firm_name || card.customer_name || 'Unknown')}
            {' · '}{card.order_id}
          </span>
          <span className="mt-1 block truncate text-[11px]" style={{ color: Colors.text.tertiary }}>{trail}</span>
          <span className="mt-2 flex flex-wrap items-center gap-2">
            <StatusPill label={`${card.passed} ${card.passed === 1 ? 'stage' : 'stages'}`} tone="positive" />
            <StatusPill label={`${card.qty_moved.toLocaleString('en-IN')} pcs`} tone="neutral" />
            {card.sent_back > 0 && (
              <StatusPill label={`${card.sent_back} ${t('supervisor.sent_back')}`} tone="caution" />
            )}
            {card.qty_short > 0 && (
              <StatusPill label={`${card.qty_short.toLocaleString('en-IN')} short`} tone="critical" />
            )}
            {card.batch_numbers.length > 0 && (
              <span className="text-[10px] font-medium" style={{ color: Colors.accent.DEFAULT }}>
                {card.batch_numbers.map((n) => `B${n}`).join(' ')}
              </span>
            )}
            <span className="ml-auto text-[10px]" style={{ color: Colors.text.tertiary }}>
              {formatIST(card.last_moved_at, { timeOnly: true })}
            </span>
          </span>
        </span>
        {open
          ? <ChevronUp className="h-[18px] w-[18px] shrink-0" style={{ color: Colors.text.tertiary }} />
          : <ChevronDown className="h-[18px] w-[18px] shrink-0" style={{ color: Colors.text.tertiary }} />}
      </button>

      {/* the whole walk, for when it has to be shown to somebody */}
      {open && (
        <div className="mt-3 border-t pt-3" style={{ borderColor: Colors.border.subtle }}>
          {card.moves.map((move) => {
            const back = move.direction === 'backward';
            const Arrow = back ? ArrowLeft : ArrowRight;
            return (
              <div key={move.id} data-testid={`move-${move.id}`} className="flex items-center gap-2 py-1">
                <Arrow
                  className="h-3 w-3 shrink-0"
                  style={{ color: back ? Colors.status.warning : Colors.accent.DEFAULT }}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[11px] font-semibold" style={{ color: Colors.text.primary }}>
                    {move.from_stage_name || '—'} → {move.to_stage_name || t('supervisor.finished')}
                  </p>
                  <p className="truncate text-[10px]" style={{ color: Colors.text.tertiary }}>
                    {move.qty_moved.toLocaleString('en-IN')} pcs
                    {move.qty_short ? ` · ${move.qty_short.toLocaleString('en-IN')} short` : ''}
                    {move.batch_number != null ? ` · B${move.batch_number}` : ''}
                    {' · '}{formatIST(move.moved_at, { timeOnly: true })}
                  </p>
                </div>
              </div>
            );
          })}
          {!!card.job_card_id && (
            <button
              type="button"
              data-testid={`open-card-${card.id}`}
              onClick={onOpenJobCard}
              className="mt-2 flex w-full items-center justify-center gap-1 border-t pt-3 text-[11px] font-semibold"
              style={{ borderColor: Colors.border.subtle, color: Colors.accent.DEFAULT }}
            >
              {t('supervisor.open_job_card')}
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
    </li>
  );
}
